import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Constants } from "@/constants";

interface CloverApp {
  name: string;
  clientId: string;
  logoUrl: string;
}

export default function CloverAppSelection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [apps, setApps] = useState<CloverApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchApps() {
      try {
        setLoading(true);
        const response = await fetch(
          `${Constants.CLOVER_SERVICE_BASE_URL}auth/clover/apps`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch apps: ${response.statusText}`);
        }

        const data = await response.json();
        setApps(data.apps || []);
      } catch (err) {
        console.error("Error fetching Clover apps:", err);
        setError(err instanceof Error ? err.message : "Failed to load apps");
      } finally {
        setLoading(false);
      }
    }

    fetchApps();
  }, []);

  const handleAppSelection = (app: CloverApp) => {
    // Build the login URL with the selected client_id and preserve other query parameters
    const baseURL = `${Constants.CLOVER_SERVICE_BASE_URL}auth/clover/login`;
    const urlParams = new URLSearchParams();

    // Add the selected client_id
    urlParams.set("client_id", app.clientId);

    // Preserve existing query parameters
    for (const [key, value] of searchParams.entries()) {
      if (key !== "client_id") {
        urlParams.set(key, value);
      }
    }

    // Build final URL
    const finalURL = `${baseURL}?${urlParams.toString()}`;

    // Redirect to the login endpoint
    window.location.href = finalURL;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-semibold mb-4">
            Loading Clover Apps...
          </h1>
          <p className="text-gray-600">
            Please wait while we fetch available apps.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-semibold text-red-800 mb-4">
              Error Loading Apps
            </h1>
            <p className="text-red-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (apps.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="text-gray-400 text-6xl mb-4">📱</div>
          <h1 className="text-2xl font-semibold text-gray-800 mb-4">
            No Apps Available
          </h1>
          <p className="text-gray-600 mb-6">
            There are currently no Clover apps available for authentication.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Select Your Clover App
          </h1>
          <p className="text-lg text-gray-600">
            Choose the Clover app you want to authenticate with:
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {apps.map((app) => (
            <div
              key={app.clientId}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
              onClick={() => handleAppSelection(app)}
            >
              <div className="p-6">
                <div className="flex flex-col items-center text-center mb-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden mb-3 bg-gray-50 border border-gray-200">
                    {app.logoUrl ? (
                      <img
                        src={app.logoUrl}
                        alt={`${app.name} logo`}
                        className="w-full h-full object-contain p-1"
                        style={{
                          // Fallback for browsers that don't support object-fit
                          backgroundSize: "contain",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center"
                        }}
                        onError={(e) => {
                          // Fallback to default icon if image fails to load
                          const target = e.target as any;
                          if (target && target.style) {
                            target.style.display = "none";
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `
                                <div class="w-full h-full bg-green-100 flex items-center justify-center">
                                  <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                                  </svg>
                                </div>
                              `;
                            }
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-green-100 flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="w-full">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {app.name}
                    </h3>
                    <p className="text-sm text-gray-500">Clover App</p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Click to authenticate
                  </span>
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate("/login")}
            className="text-gray-600 hover:text-gray-800 transition-colors"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}
